import compose from 'lodash/flowRight'
import {Suspense, useEffect} from 'react'
import type {ConnectedProps} from 'react-redux'
import {connect} from 'react-redux'

import {fetchBuildsData} from '../../../../../actions/builds'
import {memoWithDisplayName} from '../../../../../hocs/memoWithDisplayName'
import withHook from '../../../../../hocs/withHook'
import type {StatusSubscriptionProps} from '../../../../../hocs/withStatusSubscription'
import withStatusSubscription from '../../../../../hocs/withStatusSubscription'
import {useRefetchablePreloadedQuery} from '../../../../../hooks/relay/useRefetchablePreloadedQuery'
import {useQueryParams} from '../../../../../hooks/routes'
import {getBuildTypeHasNonDefaultBranchesArg} from '../../../../../rest/branches'
import {getBuildTypeArg} from '../../../../../rest/buildTypes'
import {restApi} from '../../../../../services/rest'
import {subscribeOnBuildTypeEvents} from '../../../../../utils/subscriber'
import * as SubscriptionEvents from '../../../../../utils/subscriptionEvents'
import Loader from '../../../../common/Loader/Loader'
import {submitPager} from '../../../../common/Pager/Pager.actions.base'

import BuildTypeHistory from './BuildTypeHistory'
import {buildTypeHistoryQuery, getBuildTypeHistoryQueryVariables} from './BuildTypeHistory.queries'
import {mapStateToBuildTypeHistoryProps} from './BuildTypeHistory.selectors'
import type {OwnProps} from './BuildTypeHistory.types'

const actionCreators = {
  submitPager,
  fetchBuildsData,
}
const triggerBuildEvents = [SubscriptionEvents.BUILD_TYPE_ADDED_TO_QUEUE]
const finishBuildEvents = [SubscriptionEvents.BUILD_FINISHED, SubscriptionEvents.BUILD_INTERRUPTED]

const connector = connect(mapStateToBuildTypeHistoryProps, actionCreators)

type Props = OwnProps & StatusSubscriptionProps & ConnectedProps<typeof connector>

const BuildTypeHistoryContainer = compose(
  withStatusSubscription(),
  withHook(() => {
    const {state, tag, 'agent-name-pattern': agentPattern} = useQueryParams()
    return {buildState: state, tag, agentPattern}
  }),
  connector,
  memoWithDisplayName,
  withHook(({buildTypeId, internalId, branch, mainQuery, withRunningAndQueued}: Props) => {
    const [fetchSingleBuildTypeData] =
      restApi.endpoints.getBuildTypeNormalized.useLazyQuerySubscription()
    useEffect(() => {
      if (internalId == null) {
        fetchSingleBuildTypeData(getBuildTypeArg(buildTypeId))
      }
    }, [buildTypeId, fetchSingleBuildTypeData, internalId])
    const {hasNonDefaultBranches} = restApi.endpoints.checkIfBranchExistsInBuildType.useQuery(
      getBuildTypeHasNonDefaultBranchesArg(buildTypeId),
      {selectFromResult: ({data}) => ({hasNonDefaultBranches: data})},
    )

    const [{builds}, refetch] = useRefetchablePreloadedQuery(buildTypeHistoryQuery, mainQuery)
    const hasBuilds = Boolean(builds?.count)
    useEffect(
      () =>
        internalId != null && !hasBuilds
          ? subscribeOnBuildTypeEvents(
              internalId,
              withRunningAndQueued ? triggerBuildEvents : finishBuildEvents,
              () =>
                refetch(
                  getBuildTypeHistoryQueryVariables({buildTypeId, branch, withRunningAndQueued}),
                ),
            )
          : undefined,
      [hasBuilds, internalId, refetch, buildTypeId, branch, withRunningAndQueued],
    )

    return {
      hasBranches: hasNonDefaultBranches || (branch != null && !branch.default),
      hasBuilds,
    }
  }),
)(BuildTypeHistory)

export default function BuildTypeHistoryContainerWrapper(props: OwnProps) {
  return (
    <Suspense fallback={<Loader />}>
      <BuildTypeHistoryContainer {...props} />
    </Suspense>
  )
}
